import type { FocusEvent } from "react";
import type { U2000FormStateHandlers } from "../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../reducer/formReducer/U2000FormState";
import { u2000IsComboBoxTypeaheadDebugEnabled, u2000TraceComboBoxTypeahead } from "./u2000ComboBoxTypeaheadDebug";

/**
 * Sélectionne tout le contenu éditable d'un input ou textarea au focus (clic ou Tab).
 * Aligné sur U2000AutoCompleteInput ; ignore readonly, disabled et type password.
 */
const U2000_FOCUSABLE_SELECTOR =
  'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function u2000IsVisibleFocusable(el: HTMLElement): boolean {
  if ('disabled' in el && (el as HTMLButtonElement).disabled) {
    return false;
  }
  if (el.tabIndex < 0 && !el.matches('a[href], button, input, select, textarea')) {
    return false;
  }
  return el.getClientRects().length > 0;
}

const U2000_FOCUSABLE_SELECTOR_INCLUDING_DISABLED =
  'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

function u2000IsFocusableCandidate(el: HTMLElement): boolean {
  if (el.tabIndex < 0 && !el.matches('a[href], button, input, select, textarea')) {
    return false;
  }
  return el.getClientRects().length > 0;
}

function u2000IsDisabledFocusable(el: HTMLElement): boolean {
  if ('disabled' in el && (el as HTMLButtonElement).disabled) {
    return true;
  }
  return el.getAttribute('aria-disabled') === 'true';
}

/**
 * Focus l’élément focusable suivant dans l’ordre du document (équivalent Tab).
 */
export function u2000FocusNextFocusable(from: HTMLElement): void {
  const focusables = Array.from(document.querySelectorAll<HTMLElement>(U2000_FOCUSABLE_SELECTOR)).filter(
    u2000IsVisibleFocusable,
  );
  const index = focusables.indexOf(from);
  if (index >= 0 && index < focusables.length - 1) {
    focusables[index + 1].focus();
  }
}

/**
 * Focus le prochain élément dans l’ordre Tab (y compris si le suivant immédiat
 * est brièvement disabled — ex. combo Condition avant mise à jour React du champ).
 */
export function u2000FocusNextFocusableAfterPaint(
  from: HTMLElement,
  maxAttempts = 24,
  debugComboId?: string,
): void {
  let attempts = 0;

  const run = () => {
    attempts += 1;
    const ordered = Array.from(
      document.querySelectorAll<HTMLElement>(U2000_FOCUSABLE_SELECTOR_INCLUDING_DISABLED),
    ).filter(u2000IsFocusableCandidate);
    const index = ordered.indexOf(from);
    const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null;
    const nextDisabled = next ? u2000IsDisabledFocusable(next) : null;

    if (u2000IsComboBoxTypeaheadDebugEnabled() && debugComboId) {
      u2000TraceComboBoxTypeahead(debugComboId, "focusNextAttempt", {
        attempt: attempts,
        fromTag: from.tagName,
        fromId: from.id || null,
        fromIndex: index,
        nextTag: next?.tagName ?? null,
        nextDisabled,
        nextText: next?.textContent?.trim().slice(0, 40) ?? null,
        activeTag: document.activeElement instanceof HTMLElement ? document.activeElement.tagName : null,
      });
    }

    if (index < 0 || index >= ordered.length - 1) {
      if (attempts < maxAttempts) {
        requestAnimationFrame(run);
      }
      return;
    }

    if (next && !u2000IsDisabledFocusable(next)) {
      next.focus();
      if (u2000IsComboBoxTypeaheadDebugEnabled() && debugComboId) {
        u2000TraceComboBoxTypeahead(debugComboId, "focusNextDone", {
          focusedTag: next.tagName,
          focusedText: next.textContent?.trim().slice(0, 40) ?? null,
        });
      }
      return;
    }

    if (attempts < maxAttempts) {
      requestAnimationFrame(run);
    }
  };

  requestAnimationFrame(run);
}

export function u2000SelectInputContentsOnFocus(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void {
  const el = e.target;
  if (el.readOnly || el.disabled) {
    return;
  }
  if (el instanceof HTMLInputElement && el.type === "password") {
    return;
  }
  if (typeof el.select === "function") {
    // Différé pour laisser le focus s'établir, mais NE PAS voler le focus si l'utilisateur
    // a déjà cliqué ailleurs entre-temps : `.select()` re-focalise un input non actif (Chrome).
    setTimeout(() => {
      if (document.activeElement === el) {
        el.select();
      }
    }, 0);
  }
}

/**
 * Détermine la couleur de fond et du texte de l'input selon le mode d'édition et readonly
 */
export function getInputColor(formStateHandlers: U2000FormStateHandlers<any>, readonly?: boolean): string {
  if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) return "bg-gray-100 text-gray-500";
  if (formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT) return "bg-amber-50";
  return "";
}

/**
 * Détermine la couleur de la bordure selon la validation du champ
 */
export function getInputBorderColor<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, onInput: boolean, fieldName: string): string {
  let border = "border border-gray-400";
  if ((formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName])) {
    border = "border-2 border-red-400";
  } else if (formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT && onInput) {
    border = "border-2 border-gray-400";
  }
  return border;
}

/**
 * Détermine la couleur de l'outline (focus ou erreur)
 */
export function getOutlineBorderColor<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, onInput: boolean, fieldName: string): string {
  return (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName])
    ? "outline-2 outline-offset-4 outline-red-400"
    : onInput && formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT
    ? "ring-2 ring-offset-2 ring-gray-400"
    : "";
}

/**
 * Détermine la couleur du tag selon l'index et le mode readonly
 */
export function getTagColor(formStateHandlers: U2000FormStateHandlers<any>, idx: number, readonly?: boolean): string {
  let baseColor = idx % 2 === 0 ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800";
  if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) {
    baseColor = baseColor.replace(/text-[^ ]+/, "text-gray-400");
  }
  return baseColor;
}
